/**
 * @description 메이플스토리 캐릭터 전투력 숫자 표현 규칙 (큰 숫자 축약 표현)
 * @param {number} combat_power
 * @returns {string}
 */
export function formatCombatPowerPoint({ combat_power }) {
  // 10000(만) 미만인 경우 그대로 반환
  if (combat_power < 10000) {
    return combat_power.toString().replaceAll(',', '');
  }

  // 단위별 기준값 설정
  const units = {
    조: 1000000000000,
    억: 100000000,
    만: 10000,
  };

  const result = [];
  let remainingPower = combat_power;

  // 조 단위 계산
  if (remainingPower >= units.조) {
    const jo = Math.floor(remainingPower / units.조);
    result.push(`${jo}조`);
    remainingPower %= units.조;
  }

  // 억 단위 계산
  if (remainingPower >= units.억) {
    const eok = Math.floor(remainingPower / units.억);
    result.push(`${eok}억`);
    remainingPower %= units.억;
  }

  // 만 단위 계산
  if (remainingPower >= units.만) {
    const man = Math.floor(remainingPower / units.만);
    result.push(`${man}만`);
    remainingPower %= units.만;
  }

  // 만 단위 이하 숫자 처리
  if (remainingPower > 0 && result.length > 0) {
    result.push(remainingPower.toString().replaceAll(',', ''));
  }

  return result.slice(0, 2).join(' ');
}

export function formatStringEllipsis({ string, max_length }) {
  /* 
    한글 폰트는 2배, 나머지는 1배로 계산해서 max_length를 넘지 않게 자르고, 넘으면 ...을 붙여 반환.
  */
  let length = 0;
  let result = '';
  for (const char of string) {
    // 한글 유니코드 범위: 0xAC00 ~ 0xD7A3
    const isKorean = char >= '\uAC00' && char <= '\uD7A3';
    length += isKorean ? 2 : 1;
    if (length > max_length) break;
    result += char;
  }
  if (length > max_length || result.length < string.length) {
    return result + '⋯';
  }
  return result;
}

export function formatNumberWithCommas({ string, digitGroupSize = 3 }) {
  // 기존 콤마 제거
  const raw = typeof string === 'string' ? string.replace(/,/g, '') : String(string);

  // 숫자로 변환이 가능한지 체크
  if (!/^-?\d+(\.\d+)?$/.test(raw)) {
    return string;
  }

  // leading zeros 제거 (음수, 소수점 포함 케이스 고려)
  let normalized = raw;
  const isNegative = normalized.startsWith('-');
  if (isNegative) normalized = normalized.slice(1);
  const [integer, decimal] = normalized.split('.');
  // 정수부의 leading zero 제거 (단, 0 자체는 유지)
  const trimmedInt = integer.replace(/^0+(?=\d)/, '');
  let normalizedNumber = trimmedInt;
  if (decimal !== undefined) normalizedNumber += '.' + decimal;
  if (isNegative) normalizedNumber = '-' + normalizedNumber;

  // digitGroupSize가 1 이하이면 콤마 없이 반환
  if (typeof digitGroupSize !== 'number' || digitGroupSize <= 0) {
    return normalizedNumber;
  }

  // 소수점 분리 (정규화된 값 기준)
  const [intPart, decPart] = normalizedNumber.replace('-', '').split('.');
  const regex = new RegExp(`\\B(?=(\\d{${digitGroupSize}})+(?!\\d))`, 'g');
  const formatted = intPart.replace(regex, ',');
  let result = formatted;
  if (decPart !== undefined) result += '.' + decPart;
  if (isNegative) result = '-' + result;
  return result;
}
